/* ============================================================
   LEGEND — Structure Map (price-axis heatmap). Ported from the
   root legend/sr.jsx redesign.

   A quiet vertical heat column docked just inboard of the price
   axis. Every support / resistance / flipped level paints a soft
   band at its price:
     • role     → color (support / resistance / flipped)
     • strength → band intensity (tier · weight · confluence)
     • extent   → band height (a clustered zone spans lo→hi)
     • status   → dead levels fade right out
   Bands screen-blend, so where structure stacks the column glows
   brighter — confluence you can read at a glance. The active
   floor + ceiling are auto-highlighted with a label and a faint
   line across the chart. Hover a band for full order-flow detail
   + member levels; click to stage an order off that level.

   The live server projection collapses per-level flow into
   `touches`/`trades`, so the readers below fall back across both
   the projected and the raw/mock field names.
   ============================================================ */
import React from 'react';
import { createPortal } from 'react-dom';
import { fmtPx, fmtPx4 } from '../lib/format';
import { RAIL_REACH_SOLID } from '../lib/structure';

const { useRef: useRefS, useEffect: useEffectS, useState: useStateS } = React;

const fmtVolS = (v: number) => (v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(0) + 'k' : '' + Math.round(v || 0));
function relAgoS(ts: number) {
  if (!ts) return '—';
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return s + 's';
  const mn = Math.floor(s / 60); if (mn < 60) return mn + 'm';
  const h = Math.floor(mn / 60); if (h < 24) return h + 'h'; return Math.floor(h / 24) + 'd';
}
const ROLE_NAME: Record<string, string> = { support: 'Support', resistance: 'Resistance', flipped: 'Flipped → support' };

// Read fields from either the projected Level (touches/trades/lastTestedAt) or the raw mock shape.
const lvlTouches = (b: any) => b.touches ?? b.touchCount ?? b.rejections ?? b.timesTestedAsSupport ?? 0;
const lvlTrades = (b: any) => (b.trades != null ? b.trades : (b.totalBuyTrades || 0) + (b.totalSellTrades || 0));
const lvlLast = (b: any) => b.lastTestedAt ?? b.lastTouchAt ?? b.brokeAt ?? 0;
const zoneTouches = (z: any) => z.members.reduce((a: number, b: any) => a + lvlTouches(b), 0);
const zoneTrades = (z: any) => z.members.reduce((a: number, b: any) => a + lvlTrades(b), 0);

// order-flow strength (0–1) + label, role-specific (aggregated across the zone's members)
function flowForZ(zone: any) {
  const touches = zoneTouches(zone), trades = zoneTrades(zone);
  if (zone.role === 'resistance') return { f: Math.min(1, (touches / 5) * 0.6 + Math.min(1, trades / 4000) * 0.4), label: 'rej ×' + touches, vol: trades, kind: 'sell' };
  if (zone.role === 'flipped') return { f: Math.min(1, touches / 4), label: 'retest ×' + touches, vol: trades, kind: 'flip' };
  return { f: Math.min(1, Math.min(1, touches / 30) * 0.5 + Math.min(1, trades / 4000) * 0.5), label: 'def ×' + touches, vol: trades, kind: 'buy' };
}

function stRow(k: string, v: any) {
  return React.createElement('div', { className: 'sm-st', key: k },
    React.createElement('span', null, k), React.createElement('b', { className: 'mono num' }, v));
}

// The model's reads in words, for the hover detail: the hold (and MV-5's slower holds), then MV-5's reach —
// the chance price gets to this level within the hour, with the 30-minute and by-the-close reads beside it.
const BAND_WORD: Record<string, string> = { paper: ' — paper, expect give-way', unsure: ' — unsure' };
const pc = (v: number) => Math.round(v * 100) + '%';
function mvDetailRows(mv: any) {
  if (!mv || !mv.band) return null;
  const rows: any[] = [];
  rows.push(React.createElement('div', { key: 'h', className: 'smd-mv mv-' + mv.band },
    (mv.src === 'mv5' ? 'MV-5' : 'model') + ' · ' + (mv.ind || 'level') + ' · holds ' + mv.pct + '%' + (BAND_WORD[mv.band] || '')));
  const h = mv.hold || {};
  const slower = ['3m', '5m', '15m'].filter((tf) => typeof h[tf] === 'number');
  if (slower.length) {
    rows.push(React.createElement('div', { key: 'hx', className: 'smd-mv sub' },
      'holds on ' + slower.map((tf) => tf + ' ' + pc(h[tf])).join(' · ')));
  }
  if (mv.reach) {
    const a = mv.reach.all || {};
    const more = [['30m', '30m'], ['eod', 'by close']].filter(([k]) => typeof a[k] === 'number').map(([k, lbl]) => lbl + ' ' + pc(a[k]));
    rows.push(React.createElement('div', { key: 'r', className: 'smd-mv reach' + (mv.reach.p >= RAIL_REACH_SOLID ? '' : ' lo') },
      'gets here ' + mv.reach.pct + '% within 1h' + (more.length ? ' · ' + more.join(' · ') : '')
        + (mv.reach.ind && mv.reach.ind !== mv.ind ? ' (' + mv.reach.ind + ')' : '')));
  }
  return rows;
}

// ---- hover detail popout ----
// Rendered through a PORTAL onto <body>, position: fixed. Inside the map
// column it sat in the `.smap` stacking context (z 4), so the Vision layer,
// order overlay, verdict ribbon and Vision status pill all painted over it,
// and the stage's overflow clipped it near the top. On <body> at z 60 it is
// above every chart layer and card (like the click-to-order popover
// should be), and never clipped. A rAF loop re-reads the band's rect while
// open so it stays glued through chart pans; the popup is inert to the
// pointer, so hover still resolves on the band's hit area.
function LevelDetail({ zone, lastPrice, atr, anchor }: any) {
  const ref = React.useRef<any>(null);
  React.useLayoutEffect(() => {
    let raf = 0;
    const place = () => {
      raf = requestAnimationFrame(place);
      const el = ref.current, a = anchor && anchor.current;
      if (!el || !a) return;
      const r = a.getBoundingClientRect();
      const h = el.offsetHeight || 0, vh = window.innerHeight;
      const cy = r.top + r.height / 2;
      const top = Math.max(8, Math.min(vh - h - 8, cy - h / 2));
      const right = Math.max(8, window.innerWidth - r.left + 6);
      el.style.top = top + 'px';
      el.style.right = right + 'px';
    };
    place();
    return () => cancelAnimationFrame(raf);
  }, [anchor]);
  const distAtr = atr ? (Math.abs(zone.price - lastPrice) / atr).toFixed(2) : '—';
  const flow = flowForZ(zone);
  const m0 = zone.members[0];
  const isBand = zone.lo !== zone.hi;
  // Tape-discovered provenance: wall member (absorption candle) + how the
  // current break printed, if any (CONVICTION engulf vs drift-through).
  const wall = zone.members.find((mm: any) => mm.timeframe === 'wall');
  const bq = zone.members.map((mm: any) => mm.breakQuality).find(Boolean);
  const provRows = [];
  if (wall) provRows.push(stRow('Wall candle', '×' + (wall.volMult ?? '—') + ' avg vol'));
  if (bq) provRows.push(stRow('Break', bq === 'CONVICTION' ? '⚡ conviction' : 'drift'));
  return createPortal(React.createElement('div', { className: 'sm-detail z-' + zone.role, ref },
    React.createElement('div', { className: 'smd-head' },
      React.createElement('span', { className: 'smd-role' }, ROLE_NAME[zone.role]),
      React.createElement('span', { className: 'smd-status st-' + String(zone.status).toLowerCase() }, zone.status),
      React.createElement('span', { className: 'smd-px num' }, '$' + fmtPx4(zone.price))),
    isBand ? React.createElement('div', { className: 'smd-band mono' }, '$' + fmtPx4(zone.lo) + ' – $' + fmtPx4(zone.hi) + ' zone') : null,
    React.createElement('div', { className: 'smd-meters' },
      React.createElement('div', { className: 'smd-meter' },
        React.createElement('span', null, 'Strength'),
        React.createElement('div', { className: 'smd-track' }, React.createElement('i', { style: { width: (zone.strength * 100) + '%' } })),
        React.createElement('b', { className: 'mono' }, 'T' + zone.tier + ' · w' + zone.weightSum)),
      React.createElement('div', { className: 'smd-meter' },
        React.createElement('span', null, flow.kind === 'sell' ? 'Rejection' : flow.kind === 'flip' ? 'Retest' : 'Defense'),
        React.createElement('div', { className: 'smd-track ' + flow.kind }, React.createElement('i', { style: { width: (flow.f * 100) + '%' } })),
        React.createElement('b', { className: 'mono' }, flow.label))),
    React.createElement('div', { className: 'smd-stats' },
      zone.role === 'support'
        ? [stRow('Touches', zoneTouches(zone)), stRow('Prints', fmtVolS(zoneTrades(zone))), stRow('Last test', relAgoS(lvlLast(m0))), stRow('Distance', distAtr + ' ATR')]
        : zone.role === 'resistance'
          ? [stRow('Rejections', zoneTouches(zone)), stRow('Prints', fmtVolS(zoneTrades(zone))), stRow('Last test', relAgoS(lvlLast(m0))), stRow('Distance', distAtr + ' ATR')]
          : [stRow('Tested ↑S', zoneTouches(zone) + '×'), stRow('Status', zone.status), stRow('Broke', relAgoS(lvlLast(m0))), stRow('Distance', distAtr + ' ATR')]),
    provRows.length ? React.createElement('div', { className: 'smd-stats smd-prov' }, provRows) : null,
    React.createElement('div', { className: 'smd-conf' },
      React.createElement('div', { className: 'smd-conf-h' }, (zone.confluence > 1 ? zone.confluence + ' levels · ' : '') + distAtr + ' ATR away'),
      React.createElement('div', { className: 'smd-tokens' },
        zone.members.slice(0, 10).map((mm: any, i: number) => React.createElement('span', { key: i, className: 'smtok t' + (mm.tier || 3) }, mm.type || ('$' + fmtPx(mm.price)))))),
    mvDetailRows(zone.mv),
    zone.why ? React.createElement('div', { className: 'smd-why' }, 'Shown: ' + zone.why + (zone.tag ? ' · ' + (zone.tag === 'short' ? 'SHORT candidate' : 'watch') + ' (+' + Math.round(zone.ext * 100) + '% from the session base at this level)' : '')) : null,
    React.createElement('div', { className: 'smd-cta' }, 'Click to stage an order here')), document.body);
}

// `lanes2` (the MV-5 lane): the column widens to two lanes so a zone can show MV-5's two reads side by side —
// reach (blue, left) on the side the trade is pointed at, hold (right) on every zone. Two-digit numbers there ("99"
// stands for 99–100) so each fits its half.
export function StructureMap({ chartApiRef, zones, nearSup, nearRes, atr, lastPrice, onStageOrder, lanes2 }: any) {
  const rootRef = useRefS<any>(null);
  const [hoverId, setHoverId] = useStateS<any>(null);
  const hoverBandRef = useRefS<any>(null); // the hovered .sm-bw — the detail popout's anchor
  // Density dimming: past ~14 visible zones the column reads as noise, so
  // non-floor/ceiling bands drop opacity to widen the near/far contrast.
  const dense = zones.length > 14;

  // glue every band / label / line to the price axis each frame (chart pans + lives)
  useEffectS(() => {
    let raf: number;
    const loop = () => {
      const api = chartApiRef.current, root = rootRef.current;
      if (api && root) {
        const h = root.clientHeight;
        root.querySelectorAll('[data-px]').forEach((el: any) => {
          const y = api.priceToY(parseFloat(el.getAttribute('data-px')));
          if (y == null || y < 2 || y > h - 1) { el.style.opacity = '0'; el.style.pointerEvents = 'none'; }
          else { el.style.opacity = ''; el.style.pointerEvents = ''; el.style.transform = 'translateY(' + y + 'px)'; }
        });
        root.querySelectorAll('[data-hi]').forEach((el: any) => {
          const yHi = api.priceToY(parseFloat(el.getAttribute('data-hi')));
          const yLo = api.priceToY(parseFloat(el.getAttribute('data-lo')));
          if (yHi != null && yLo != null) el.style.height = Math.max(9, Math.abs(yLo - yHi)) + 'px';
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const byId: Record<string, any> = {}; zones.forEach((z: any) => { byId[z.id] = z; });
  const hoverZone = hoverId ? byId[hoverId] : null;

  function bandEl(z: any) {
    const st = String(z.status).toLowerCase();
    const dead = z.status === 'LOST' || z.status === 'BROKEN';
    const isNear = z === nearSup || z === nearRes;
    const isBand = z.lo !== z.hi;
    const hasWall = z.members.some((mm: any) => mm.timeframe === 'wall');
    let op = Math.min(0.82, (dead ? 0.12 : 0.18 + z.strength * 0.34) * (isNear ? 1.5 : 1));
    if (dense && !isNear) op *= 0.55;
    const distAtr = atr ? (Math.abs(z.price - lastPrice) / atr).toFixed(1) : '—';
    const bandProps: any = { className: 'sm-band', style: { opacity: op } };
    if (isBand) { bandProps['data-hi'] = z.hi; bandProps['data-lo'] = z.lo; }
    return React.createElement('div', {
      key: z.id, 'data-px': z.price,
      className: 'sm-bw z-' + z.role + ' st-' + st + (isNear ? ' near' : '') + (dead ? ' dead' : '') + (z.tier === 1 ? ' t1' : '') + (hasWall ? ' haswall' : '') + (hoverId === z.id ? ' hot' : ''),
    },
      React.createElement('span', bandProps),
      hasWall ? React.createElement('span', { className: 'sm-wglyph', title: 'Tape-discovered wall (absorption candle)' }, 'W') : null,
      // Main mode: extension tag — the run from the session base the stock would have made at this level.
      z.tag ? React.createElement('span', {
        className: 'sm-tag ' + z.tag + (isNear ? ' near' : ''),
        title: (z.tag === 'short' ? 'Short candidate' : 'Watch') + ' — reaching this level means a +' + Math.round(z.ext * 100) + '% run from the session base',
      }, z.tag === 'short' ? 'SHORT' : 'WATCH') : null,
      // The model's read rides INSIDE the column (operator, 2026-09-18). It used
      // to hang off `right: 100%` as an "AI 87" chip in the chart area, which
      // both named the speaker rather than the claim — the same defect PR #696
      // fixed on the chart markers — and broke the one thing a rack is for:
      // scanning one vertical line of numbers. The level's name stays in the
      // tooltip and the hover detail, where there is room for it.
      z.mv && z.mv.band ? React.createElement('span', {
        className: 'sm-ai mv-' + z.mv.band + ' z-' + z.role,
        title: (z.mv.src === 'mv5' ? 'MV-5: ' : 'model: ') + (z.mv.ind || 'level') + ' holds ' + z.mv.pct + '%' + (BAND_WORD[z.mv.band] || ''),
      }, String(lanes2 ? Math.min(99, z.mv.pct) : z.mv.pct)) : null,
      // MV-5's reach: will price get here within the hour — only on the side the trade is pointed at.
      lanes2 && z.mv && z.mv.reach && z.mv.target ? React.createElement('span', {
        className: 'sm-rc' + (z.mv.reach.p >= RAIL_REACH_SOLID ? '' : ' lo'),
        title: 'MV-5: price gets here within 1h ' + z.mv.reach.pct + '% (' + (z.mv.reach.ind || 'level') + ')',
      }, String(Math.min(99, z.mv.reach.pct))) : null,
      React.createElement('div', {
        className: 'sm-hit', title: 'Click to stage an order · hover for detail',
        onMouseEnter: (e: any) => { hoverBandRef.current = e.currentTarget.parentElement; setHoverId(z.id); },
        onMouseLeave: () => setHoverId((h: any) => (h === z.id ? null : h)),
        onClick: () => onStageOrder && onStageOrder(z.price),
      }),
      isNear ? React.createElement('span', { className: 'sm-lab' },
        React.createElement('b', null, z === nearRes ? 'CEIL' : 'FLOOR'),
        React.createElement('span', { className: 'sm-lab-px num' }, fmtPx4(z.price)),
        React.createElement('span', { className: 'sm-lab-d mono' }, distAtr + ' ATR')) : null,
      hoverId === z.id ? React.createElement(LevelDetail, { zone: z, lastPrice, atr, anchor: hoverBandRef }) : null);
  }

  // projection lines: active floor + ceiling always; hovered band when distinct
  const lines: any[] = [];
  const pushLine = (z: any, cls: string) => { if (z) lines.push(React.createElement('div', { key: 'ln-' + cls + '-' + z.id, className: 'sm-line z-' + z.role + ' ' + cls, 'data-px': z.price })); };
  pushLine(nearSup, 'floor');
  pushLine(nearRes, 'ceil');
  if (hoverZone && hoverZone !== nearSup && hoverZone !== nearRes) pushLine(hoverZone, 'hover');

  return React.createElement('div', { className: 'smap' + (lanes2 ? ' lanes2' : ''), ref: rootRef },
    React.createElement('div', { className: 'smap-col' }),
    // The display mode + level kinds are chosen in the Indicators card (chart
    // header); the old button column here sat on top of the price axis.
    lines,
    React.createElement('div', { className: 'sm-price', 'data-px': lastPrice },
      React.createElement('span', { className: 'smp-dot' }),
      React.createElement('span', { className: 'smp-lab num' }, fmtPx4(lastPrice))),
    zones.map(bandEl));
}
